import { jsPDF } from "jspdf";
import moment from "moment";

const EXPORT_STATUSES = new Set([
  "accepted",
  "startduty",
  "endduty",
  "completed",
]);

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const textValue = (value, fallback = "-") => {
  if (value === 0) return "0";
  if (value === false) return "No";
  if (value === true) return "Yes";
  if (value == null) return fallback;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : fallback;
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY hh:mm A") : "-";
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "-";
};

const formatAmount = (value) => {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `Rs. ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(num)}`;
};

const isFiniteAmount = (value) =>
  value != null && value !== "" && Number.isFinite(Number(value));

const formatDeductionAmount = (value) =>
  isFiniteAmount(value) ? `- ${formatAmount(value)}` : "-";

const formatPerDayAmount = (value) =>
  isFiniteAmount(value) ? `${formatAmount(value)} Per day` : "-";

const safeFilePart = (value, fallback = "export") =>
  String(value || fallback)
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || fallback;

const getVisitId = (visit) => {
  if (!visit) return "";
  if (typeof visit._id === "string") return visit._id;
  if (visit._id?.$oid) return String(visit._id.$oid);
  return String(visit._id || visit.id || "");
};

export const getPartnerIdForExport = (visit) => {
  const rawId = visit?.appointmentType?.partnerID || visit?.partnerID || "";
  if (typeof rawId === "string") return rawId;
  if (rawId && typeof rawId === "object") {
    return String(rawId?._id || rawId?.id || rawId?.$oid || "");
  }
  return String(rawId || "");
};

const getPatientName = (visit) =>
  textValue(
    visit?.patient?.name ||
      visit?.patientInfo?.personalInfo?.basicInfo?.nameInfo?.fullName,
    "Unknown Patient",
  );

const getSpecialistName = (visit) =>
  textValue(
    visit?.dutyStaff?.name ||
      visit?.specialistInfo?.name ||
      visit?.assessmentStaff?.name,
    "Unassigned",
  );

const getPaymentModeLabel = (visit) => {
  const mode = String(visit?.paymentMode || "")
    .trim()
    .toLowerCase();
  if (mode === "online") return "Online";
  if (mode === "location_qr") return "On Location QR";
  if (mode === "daily_qr") return "Daily QR";
  if (visit?.payOnLocation === true) return "On Location QR";
  return textValue(visit?.paymentMode, "-");
};

const getStatusLabel = (status) => {
  const key = normalizeStatus(status);
  if (key === "accepted") return "Accepted / Confirmed";
  if (key === "startduty") return "Start Duty";
  if (key === "endduty") return "End Duty";
  if (key === "completed") return "Completed";
  return textValue(status, "-");
};

const getBookedByLabel = (bookedBy) => {
  const value = textValue(bookedBy);
  return normalizeStatus(value) === "self" ? "patient" : value;
};

const getCreatedAt = (visit) => {
  const timelineCreated = Array.isArray(visit?.timeline)
    ? visit.timeline.find((item) =>
        /created|booked/i.test(String(item?.event || "")),
      )
    : null;
  return timelineCreated?.dateTime || visit?.createdAt || null;
};

const getPaidInfo = (visit) => {
  const paidInfo = visit?.paymentInfo?.paidInfo || {};
  const assessmentPaid = visit?.paymentInfo?.assessment?.paidInfo || {};
  const locationQr = visit?.paymentInfo?.locationQr || {};
  const sessions = Array.isArray(visit?.paymentInfo?.sessions)
    ? visit.paymentInfo.sessions
    : [];
  const duties = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
  const paymentIds = [
    paidInfo?.metadata?.razorpay_payment_id,
    paidInfo?.paymentID,
    visit?.paymentInfo?.paymentID,
    assessmentPaid?.metadata?.razorpay_payment_id,
    assessmentPaid?.paymentID,
    locationQr?.paymentID,
    ...sessions.flatMap((session) => [
      session?.paidInfo?.metadata?.razorpay_payment_id,
      session?.paidInfo?.paymentID,
      session?.paymentID,
    ]),
    ...duties.map((duty) => duty?.payment?.paymentID),
  ]
    .map((paymentId) => textValue(paymentId, ""))
    .filter(Boolean);

  const paid =
    paidInfo?.paid === true ||
    assessmentPaid?.paid === true ||
    String(locationQr?.status || "")
      .trim()
      .toLowerCase() === "paid";

  const paidDateTime =
    paidInfo?.paidDateTime ||
    assessmentPaid?.paidDateTime ||
    locationQr?.paidDateTime ||
    null;

  const amountPaid =
    paidInfo?.amountPaid ??
    assessmentPaid?.amountPaid ??
    visit?.paymentInfo?.grandTotal ??
    visit?.paymentInfo?.assessment?.grandTotal ??
    locationQr?.amount ??
    null;

  return {
    paid,
    paidDateTime,
    amountPaid,
    paymentId: Array.from(new Set(paymentIds)).join(", ") || "-",
  };
};

const formatServiceValues = (values) => {
  if (values == null) return "-";
  if (typeof values === "string" || typeof values === "number") {
    return textValue(values);
  }
  if (Array.isArray(values)) {
    const parts = values
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "string" || typeof item === "number") {
          return textValue(item, "");
        }
        if (typeof item === "object") {
          return Object.entries(item)
            .map(([key, value]) => {
              if (value == null || value === "") return "";
              if (typeof value === "object") {
                try {
                  return `${key}: ${JSON.stringify(value)}`;
                } catch (error) {
                  return `${key}: [object]`;
                }
              }
              return `${key}: ${value}`;
            })
            .filter(Boolean)
            .join(", ");
        }
        return "";
      })
      .filter(Boolean);
    return parts.length ? parts.join(" | ") : "-";
  }
  if (typeof values === "object") {
    const parts = Object.entries(values)
      .map(([key, value]) => {
        if (value == null || value === "") return "";
        if (typeof value === "object") {
          try {
            return `${key}: ${JSON.stringify(value)}`;
          } catch (error) {
            return `${key}: [object]`;
          }
        }
        return `${key}: ${value}`;
      })
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "-";
  }
  return "-";
};

const getPayoutSummary = (duty) => {
  const vendor = duty?.payout?.payoutToTheVendor || {};
  const company = duty?.payout?.commisionToTheCompany || {};
  const vendorAmount = vendor?.amountToTransfer ?? vendor?.netPayout ?? null;
  const companyAmount =
    company?.totalDailyOutflow ??
    (isFiniteAmount(company?.netCommission) &&
    isFiniteAmount(company?.routingFee)
      ? Number(company.netCommission) + Number(company.routingFee)
      : company?.netCommission ?? null);
  return {
    overallStatus: textValue(duty?.payout?.status, "pending"),
    totalDailyOutflow: duty?.payout?.totalDailyOutflow ?? null,
    vendorNetPayout: vendor?.netPayout ?? null,
    vendorTransactionCharge: vendor?.vendorTransactionCharge ?? null,
    vendorAmount,
    vendorStatus: textValue(vendor?.status, "-"),
    vendorTransferId: textValue(vendor?.transferID, "-"),
    vendorTransferAt: textValue(vendor?.transferDateTime, "-"),
    vendorBonus: vendor?.bonus ?? 0,
    vendorPenalty: vendor?.penalty ?? 0,
    companyNetCommission: company?.netCommission ?? null,
    companyRoutingFee: company?.routingFee ?? null,
    companyAmount,
    companyStatus: textValue(company?.status, "-"),
    companyTransferId: textValue(company?.transferID, "-"),
  };
};

const isPaidStatus = (value) => normalizeStatus(value) === "paid";

const getPaymentGroupSummary = (group) => {
  if (!group || typeof group !== "object") {
    return { received: 0, gatewayDeductions: null };
  }

  const paidInfo = group?.paidInfo || {};
  if (paidInfo?.paid !== true) {
    return { received: 0, gatewayDeductions: null };
  }

  const receivedRaw =
    paidInfo?.amountPaid ?? group?.grandTotal ?? group?.amount ?? 0;
  const depositedRaw = group?.amountDepositedOnRazorpay;
  const received = Number(receivedRaw);
  const deposited = Number(depositedRaw);
  return {
    received: isFiniteAmount(receivedRaw) ? received : 0,
    gatewayDeductions:
      isFiniteAmount(receivedRaw) && isFiniteAmount(depositedRaw)
        ? Math.max(received - deposited, 0)
        : null,
  };
};

const getVisitPaymentSummary = (visit) => {
  const duties = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
  if (normalizeStatus(visit?.paymentMode) === "dailyqr") {
    return duties.reduce(
      (summary, duty) => {
        if (!isPaidStatus(duty?.payment?.status)) return summary;
        const receivedRaw = duty?.payment?.amount;
        const afterGatewayRaw = duty?.payout?.totalDailyOutflow;
        const received = Number(receivedRaw);
        const afterGateway = Number(afterGatewayRaw);
        if (isFiniteAmount(receivedRaw)) summary.received += received;
        if (isFiniteAmount(receivedRaw) && isFiniteAmount(afterGatewayRaw)) {
          summary.gatewayDeductions =
            Number(summary.gatewayDeductions || 0) +
            Math.max(received - afterGateway, 0);
        }
        return summary;
      },
      { received: 0, gatewayDeductions: null },
    );
  }

  const paidSessions = Array.isArray(visit?.paymentInfo?.sessions)
    ? visit.paymentInfo.sessions.filter(
        (session) => session?.paidInfo?.paid === true,
      )
    : [];
  const paidGroups = [
    ...(visit?.paymentInfo?.assessment?.paidInfo?.paid === true
      ? [visit.paymentInfo.assessment]
      : []),
    ...paidSessions,
  ];
  if (paidGroups.length) {
    return paidGroups.reduce(
      (summary, session) => {
        const sessionSummary = getPaymentGroupSummary(session);
        summary.received += sessionSummary.received;
        if (isFiniteAmount(sessionSummary.gatewayDeductions)) {
          summary.gatewayDeductions =
            Number(summary.gatewayDeductions || 0) +
            Number(sessionSummary.gatewayDeductions);
        }
        return summary;
      },
      { received: 0, gatewayDeductions: null },
    );
  }

  const rootSummary = getPaymentGroupSummary(visit?.paymentInfo);
  if (rootSummary.received > 0) return rootSummary;

  const paidInfo = getPaidInfo(visit);
  const received = paidInfo.paid ? Number(paidInfo.amountPaid) : 0;
  return {
    received: Number.isFinite(received) ? received : 0,
    gatewayDeductions: null,
  };
};

const getDutyCountForPaymentAllocation = (visit) => {
  const duties = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
  if (duties.length) return duties.length;

  const configuredDays =
    visit?.paymentInfo?.totalDays ||
    visit?.visitInfo?.days ||
    visit?.visitInfo?.totalDays;
  return isFiniteAmount(configuredDays) && Number(configuredDays) > 0
    ? Number(configuredDays)
    : 1;
};

export const getDutyTransactionSummary = (visit, duty) => {
  const payout = getPayoutSummary(duty);
  const paymentMode = normalizeStatus(visit?.paymentMode);
  const dailyPaymentStatus = normalizeStatus(duty?.payment?.status);
  const dailyPaymentAmount = duty?.payment?.amount;
  const hasDailyPayment =
    paymentMode === "dailyqr" ||
    duty?.payment?.required === true ||
    (isFiniteAmount(dailyPaymentAmount) && dailyPaymentStatus !== "notrequired");
  const paymentSummary = getVisitPaymentSummary(visit);
  const dutyCount = getDutyCountForPaymentAllocation(visit);

  let patientPaid = null;
  let gatewayDeductions = null;

  if (hasDailyPayment) {
    if (isFiniteAmount(dailyPaymentAmount)) {
      patientPaid = Number(dailyPaymentAmount);
      if (
        dailyPaymentStatus === "paid" &&
        isFiniteAmount(payout.totalDailyOutflow)
      ) {
        gatewayDeductions = Math.max(
          patientPaid - Number(payout.totalDailyOutflow),
          0,
        );
      }
    }
  } else {
    if (isFiniteAmount(paymentSummary.received) && paymentSummary.received > 0) {
      patientPaid = Number(paymentSummary.received) / dutyCount;
    } else {
      const fallbackTotal =
        visit?.paymentInfo?.grandTotal ??
        visit?.paymentInfo?.totalFees ??
        (isFiniteAmount(visit?.paymentInfo?.feePerDay)
          ? Number(visit.paymentInfo.feePerDay) * dutyCount
          : null);
      if (isFiniteAmount(fallbackTotal)) {
        patientPaid = Number(fallbackTotal) / dutyCount;
      }
    }

    if (isFiniteAmount(paymentSummary.gatewayDeductions)) {
      gatewayDeductions = Number(paymentSummary.gatewayDeductions) / dutyCount;
    } else if (
      paymentSummary.received > 0 &&
      isFiniteAmount(payout.totalDailyOutflow) &&
      isFiniteAmount(patientPaid)
    ) {
      gatewayDeductions = Math.max(
        Number(patientPaid) - Number(payout.totalDailyOutflow),
        0,
      );
    }
  }

  const amountAfterGateway = isFiniteAmount(payout.totalDailyOutflow)
    ? Number(payout.totalDailyOutflow)
    : isFiniteAmount(patientPaid) && isFiniteAmount(gatewayDeductions)
      ? Math.max(Number(patientPaid) - Number(gatewayDeductions), 0)
      : null;
  const companyNetReceives = payout.companyNetCommission;
  const companyTransferCharge = payout.companyRoutingFee;
  const companyTotal = isFiniteAmount(payout.companyAmount)
    ? Number(payout.companyAmount)
    : isFiniteAmount(companyNetReceives) && isFiniteAmount(companyTransferCharge)
      ? Number(companyNetReceives) + Number(companyTransferCharge)
      : null;
  const partnerNetReceives = payout.vendorNetPayout;
  const partnerGrossShare =
    isFiniteAmount(amountAfterGateway) && isFiniteAmount(companyTotal)
      ? Math.max(Number(amountAfterGateway) - Number(companyTotal), 0)
      : isFiniteAmount(partnerNetReceives) &&
          isFiniteAmount(payout.vendorTransactionCharge)
        ? Number(partnerNetReceives) +
          Number(payout.vendorTransactionCharge)
        : null;
  const partnerTransferCharge = isFiniteAmount(payout.vendorTransactionCharge)
    ? Number(payout.vendorTransactionCharge)
    : isFiniteAmount(partnerGrossShare) && isFiniteAmount(partnerNetReceives)
      ? Math.max(Number(partnerGrossShare) - Number(partnerNetReceives), 0)
      : null;

  return {
    patientPaid,
    gatewayDeductions,
    amountAfterGateway,
    companyNetReceives,
    companyTransferCharge,
    companyTotal,
    partnerNetReceives,
    partnerTransferCharge,
    partnerGrossShare,
  };
};

const isDutyCompleted = (duty) => {
  const status = normalizeStatus(duty?.serviceStatus);
  return (
    duty?.endDuty?.verified === true ||
    ["completed", "endduty", "closed", "forceclosed"].includes(status)
  );
};

const getTransferReconciliationStatus = (duty) => {
  const payout = getPayoutSummary(duty);
  const vendorStatus = normalizeStatus(payout.vendorStatus);
  const vendorAmount = Number(payout.vendorAmount);
  const hasPayoutData =
    isFiniteAmount(payout.vendorNetPayout) || isFiniteAmount(payout.vendorAmount);

  if (vendorStatus === "processed") return "Processed";
  if (!isDutyCompleted(duty)) {
    return vendorStatus === "pending" && isFiniteAmount(payout.vendorAmount) && vendorAmount > 0
      ? "Not yet eligible"
      : "Duty not completed";
  }
  if (!hasPayoutData) return "Missing payout data";
  if (
    vendorStatus === "pending" &&
    isFiniteAmount(payout.vendorAmount) &&
    vendorAmount > 0
  ) {
    return "Pending transfer";
  }
  if (
    vendorStatus === "pending" &&
    isFiniteAmount(payout.vendorAmount) &&
    vendorAmount <= 0
  ) {
    return "No transfer required";
  }
  if (vendorStatus && vendorStatus !== "-") {
    return textValue(payout.vendorStatus);
  }
  return "Transfer status unavailable";
};

const uniqueVisits = (visits = []) => {
  const map = new Map();
  (Array.isArray(visits) ? visits : []).forEach((item) => {
    const visit = item?.parentVisit || item;
    if (!visit) return;
    const id = getVisitId(visit);
    if (!id) return;
    if (!map.has(id)) map.set(id, visit);
  });
  return Array.from(map.values());
};

const getPartnerDetails = (partner, visits) => {
  const fallbackName = visits
    .map((visit) => textValue(visit?.appointmentType?.partnerName, ""))
    .find(Boolean);
  const addressInfo = partner?.addressInfo || {};
  const fullAddress =
    textValue(addressInfo?.fullAddress, "") ||
    [
      addressInfo?.address1,
      addressInfo?.address2,
      addressInfo?.city,
      addressInfo?.state,
      addressInfo?.pincode,
    ]
      .map((part) => textValue(part, ""))
      .filter(Boolean)
      .join(", ");
  const phones = [
    partner?.basicInfo?.phone,
    ...(Array.isArray(partner?.contactInfo)
      ? partner.contactInfo.map((contact) => contact?.phone)
      : []),
  ]
    .map((phone) => textValue(phone, ""))
    .filter(Boolean);

  return {
    name: textValue(partner?.basicInfo?.name || fallbackName, "Partner"),
    bannerImage: textValue(partner?.basicInfo?.bannerImage, ""),
    address: fullAddress,
    phones: Array.from(new Set(phones)).join(", "),
    gstin: textValue(partner?.verificationDocuments?.GST?.number, ""),
  };
};

const fetchImageAsDataUrl = async (src) => {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
};

const getImageDimensions = (src) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () =>
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve(null);
    image.src = src;
  });

const loadImageForPdf = async (src) => {
  const imageSrc = textValue(src, "");
  if (!imageSrc) return null;
  const dataUrl = /^data:image\//i.test(imageSrc)
    ? imageSrc
    : await fetchImageAsDataUrl(imageSrc);
  if (!dataUrl) return null;
  const dimensions = await getImageDimensions(dataUrl);
  return {
    dataUrl,
    format: dataUrl.toLowerCase().includes("image/png") ? "PNG" : "JPEG",
    width: dimensions?.width || 100,
    height: dimensions?.height || 50,
  };
};

const getExportSummary = (visits) => {
  let totalDutyDays = 0;
  let totalPayout = 0;
  let completedBookings = 0;
  let patientReceived = 0;
  let gatewayDeductions = 0;
  let gatewayDataAvailable = false;
  let companyPayout = 0;
  let transferredPayout = 0;
  let pendingPayout = 0;
  let pendingTransfers = 0;
  let missingPayouts = 0;
  let notYetEligible = 0;

  visits.forEach((visit) => {
    if (normalizeStatus(visit?.status) === "completed") completedBookings += 1;
    const payment = getVisitPaymentSummary(visit);
    patientReceived += payment.received;
    if (isFiniteAmount(payment.gatewayDeductions)) {
      gatewayDataAvailable = true;
      gatewayDeductions += Number(payment.gatewayDeductions);
    }
    const duties = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
    totalDutyDays += duties.length;
    duties.forEach((duty) => {
      const payout = getPayoutSummary(duty);
      const amount = Number(payout.vendorAmount);
      if (isFiniteAmount(payout.vendorAmount)) totalPayout += amount;
      const companyAmount = Number(payout.companyAmount);
      if (isFiniteAmount(payout.companyAmount)) companyPayout += companyAmount;

      const reconciliationStatus = getTransferReconciliationStatus(duty);
      if (
        reconciliationStatus === "Processed" &&
        isFiniteAmount(payout.vendorAmount)
      ) {
        transferredPayout += amount;
      } else if (reconciliationStatus === "Pending transfer") {
        pendingTransfers += 1;
        if (isFiniteAmount(payout.vendorAmount)) pendingPayout += amount;
      } else if (reconciliationStatus === "Missing payout data") {
        missingPayouts += 1;
      } else if (reconciliationStatus === "Not yet eligible") {
        notYetEligible += 1;
      }
    });
  });

  return {
    totalBookings: visits.length,
    completedBookings,
    totalDutyDays,
    totalPayout,
    patientReceived,
    gatewayDeductions: gatewayDataAvailable ? gatewayDeductions : null,
    companyPayout,
    transferredPayout,
    pendingPayout,
    pendingTransfers,
    missingPayouts,
    notYetEligible,
  };
};

export const summarizeDetailedBookingExport = (visits = []) =>
  getExportSummary(filterExportableBookings(visits));

export const filterExportableBookings = (visits = []) =>
  uniqueVisits(visits).filter((visit) =>
    EXPORT_STATUSES.has(normalizeStatus(visit?.status)),
  );

const ensureSpace = (doc, currentY, needed = 40) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + needed <= pageHeight - 36) return currentY;
  doc.addPage();
  return 40;
};

const drawSectionTitle = (doc, title, y) => {
  const nextY = ensureSpace(doc, y + 8, 24);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(30, 58, 95);
  doc.text(title, 40, nextY);
  doc.setDrawColor(67, 97, 238);
  doc.setLineWidth(0.6);
  doc.line(40, nextY + 4, 555, nextY + 4);
  doc.setTextColor(20, 20, 20);
  return nextY + 16;
};

const drawKeyValueRows = (doc, rows, startY) => {
  let y = startY;
  doc.setFontSize(9);
  rows.forEach(([label, value], index) => {
    const lines = doc.splitTextToSize(textValue(value), 382);
    const rowHeight = Math.max(18, lines.length * 10 + 8);
    y = ensureSpace(doc, y, rowHeight);
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.4);
    doc.setFillColor(
      index % 2 === 0 ? 248 : 255,
      index % 2 === 0 ? 250 : 255,
      index % 2 === 0 ? 252 : 255,
    );
    doc.rect(40, y - 4, 515, rowHeight, "FD");
    doc.line(160, y - 4, 160, y - 4 + rowHeight);
    doc.setFont("helvetica", "bold");
    doc.text(`${label}:`, 46, y + 7);
    doc.setFont("helvetica", "normal");
    doc.text(lines, 168, y + 7);
    y += rowHeight;
  });
  return y + 8;
};

const drawPartnerHeader = async (doc, partnerDetails, startY) => {
  let y = startY;
  const centerX = doc.internal.pageSize.getWidth() / 2;
  const logo = await loadImageForPdf(partnerDetails.bannerImage);

  if (logo?.dataUrl) {
    const logoHeight = 64;
    const logoWidth = Math.min(
      200,
      Math.max(100, (logo.width / Math.max(logo.height, 1)) * logoHeight),
    );
    doc.addImage(
      logo.dataUrl,
      logo.format,
      centerX - logoWidth / 2,
      y,
      logoWidth,
      logoHeight,
    );
    y += logoHeight + 10;
  }

  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.text(partnerDetails.name, centerX, y, { align: "center" });
  y += 15;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const detailLines = [];
  if (partnerDetails.address) {
    detailLines.push(...doc.splitTextToSize(partnerDetails.address, 430));
  }
  if (partnerDetails.phones) detailLines.push(`Phone: ${partnerDetails.phones}`);
  if (partnerDetails.gstin) detailLines.push(`GSTIN: ${partnerDetails.gstin}`);
  if (detailLines.length) {
    doc.text(detailLines, centerX, y, { align: "center" });
    y += detailLines.length * 11 + 4;
  }

  doc.setDrawColor(112, 95, 64);
  doc.setLineWidth(0.7);
  doc.line(32, y, 563, y);
  return y + 20;
};

const drawSummaryCards = (doc, summary, startY) => {
  const cards = [
    ["Total Bookings", String(summary.totalBookings)],
    ["Duty Days", String(summary.totalDutyDays)],
    ["Total Payout", formatAmount(summary.totalPayout)],
  ];
  const left = 40;
  const gap = 8;
  const cardsPerRow = 3;
  const cardWidth = (515 - gap * (cardsPerRow - 1)) / cardsPerRow;
  const cardHeight = 48;

  cards.forEach(([label, value], index) => {
    const rowIndex = Math.floor(index / cardsPerRow);
    const columnIndex = index % cardsPerRow;
    const x = left + columnIndex * (cardWidth + gap);
    const y = startY + rowIndex * (cardHeight + gap);
    doc.setFillColor(238, 242, 255);
    doc.setDrawColor(199, 210, 254);
    doc.roundedRect(x, y, cardWidth, cardHeight, 4, 4, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(label, x + 8, y + 15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(30, 58, 95);
    doc.text(value, x + 8, y + 35);
  });

  doc.setTextColor(20, 20, 20);
  const rowCount = Math.ceil(cards.length / cardsPerRow);
  return startY + rowCount * cardHeight + (rowCount - 1) * gap + 20;
};

const drawSimpleTable = (doc, headers, rows, startY, colWidths) => {
  let y = ensureSpace(doc, startY, 28);
  const left = 44;
  const rowPad = 4;
  const fontSize = 8;
  const usableWidth = colWidths.reduce((sum, width) => sum + width, 0);

  const drawRow = (cells, isHeader = false) => {
    const wrapped = cells.map((cell, index) =>
      doc.splitTextToSize(textValue(cell), Math.max(colWidths[index] - 8, 20)),
    );
    const rowHeight =
      Math.max(...wrapped.map((lines) => lines.length), 1) * 10 + rowPad * 2;

    y = ensureSpace(doc, y, rowHeight + 2);
    doc.setDrawColor(203, 213, 225);
    doc.setFillColor(isHeader ? 67 : 255, isHeader ? 97 : 255, isHeader ? 238 : 255);
    doc.rect(left, y, usableWidth, rowHeight, isHeader ? "FD" : "S");

    let x = left;
    colWidths.forEach((width, index) => {
      if (index > 0) {
        doc.line(x, y, x, y + rowHeight);
      }
      doc.setFont("helvetica", isHeader ? "bold" : "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(isHeader ? 255 : 20, isHeader ? 255 : 20, isHeader ? 255 : 20);
      doc.text(wrapped[index], x + 4, y + rowPad + 8);
      x += width;
    });

    y += rowHeight;
  };

  drawRow(headers, true);
  if (!rows.length) {
    drawRow(headers.map((_, index) => (index === 0 ? "-" : index === 1 ? "No records" : "-")));
  } else {
    rows.forEach((row) => drawRow(row));
  }

  doc.setTextColor(20, 20, 20);
  return y + 10;
};

const getTransferIssueRows = (visits) => {
  const rows = [];
  visits.forEach((visit) => {
    const duties = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
    duties.forEach((duty, dutyIndex) => {
      const status = getTransferReconciliationStatus(duty);
      if (
        [
          "Processed",
          "Not yet eligible",
          "Duty not completed",
          "No transfer required",
        ].includes(status)
      ) {
        return;
      }
      const payout = getPayoutSummary(duty);
      rows.push([
        textValue(visit?.jobID || getVisitId(visit)),
        getPatientName(visit),
        textValue(duty?.date || `Day ${duty?.day || dutyIndex + 1}`),
        formatAmount(payout.vendorAmount),
        payout.vendorStatus,
        status,
      ]);
    });
  });
  return rows;
};

const drawVisitBlock = (doc, visit, index, startY) => {
  let y = ensureSpace(doc, startY, 48);
  const paidInfo = getPaidInfo(visit);
  const bookingId = textValue(visit?.jobID || getVisitId(visit));
  const services = Array.isArray(visit?.visitInfo?.services)
    ? visit.visitInfo.services
        .map((service) =>
          typeof service === "string"
            ? service
            : service?.name || service?.service || "",
        )
        .filter(Boolean)
        .join(", ")
    : "-";

  doc.setFillColor(238, 242, 255);
  doc.roundedRect(36, y - 12, 523, 28, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(30, 58, 95);
  doc.text(
    `${index + 1}. Booking ID: ${bookingId} | Patient Name: ${getPatientName(visit)}`,
    44,
    y + 6,
  );
  doc.setTextColor(20, 20, 20);
  y += 28;

  y = drawSectionTitle(doc, "Booking Overview", y);
  y = drawKeyValueRows(
    doc,
    [
      ["Visit Status", getStatusLabel(visit?.status)],
      ["Booking Created", formatDateTime(getCreatedAt(visit))],
      [
        "Service Window",
        `${formatDate(visit?.visitInfo?.startDate)} - ${formatDate(
          visit?.visitInfo?.endDate,
        )}`,
      ],
      ["Duty Type", textValue(visit?.dutyType)],
      [
        "Shift / Slot",
        textValue(visit?.visitInfo?.shiftType || visit?.visitInfo?.slot),
      ],
      ["Specialist Role", textValue(visit?.specialistInfo?.role)],
      ["Assigned HCP", getSpecialistName(visit)],
      ["Standby HCP", textValue(visit?.standBySpecialist?.name)],
      ["Booked By", getBookedByLabel(visit?.bookedBy)],
      ["Services", services || "-"],
      [
        "Address",
        [
          visit?.visitInfo?.addressInfo?.address1,
          visit?.visitInfo?.addressInfo?.address2,
          visit?.visitInfo?.addressInfo?.landmark,
          visit?.visitInfo?.addressInfo?.city,
          visit?.visitInfo?.addressInfo?.district,
          visit?.visitInfo?.addressInfo?.state,
          visit?.visitInfo?.addressInfo?.pincode,
        ]
          .map((part) => textValue(part, ""))
          .filter(Boolean)
          .join(", ") || "-",
      ],
    ],
    y,
  );

  y = drawSectionTitle(doc, "Payment Details", y);
  y = drawKeyValueRows(
    doc,
    [
      ["Payment Mode", getPaymentModeLabel(visit)],
      ["Payment ID", paidInfo.paymentId],
      ["Paid", paidInfo.paid ? "Yes" : "No"],
      ["Paid On", formatDateTime(paidInfo.paidDateTime)],
      ["Amount", formatAmount(paidInfo.amountPaid)],
    ],
    y,
  );

  const duties = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
  y = drawSectionTitle(doc, `Day-wise Duty Details (${duties.length})`, y);

  if (!duties.length) {
    y = drawKeyValueRows(doc, [["Duties", "No duty days available"]], y);
  } else {
    duties.forEach((duty, dutyIndex) => {
      const transaction = getDutyTransactionSummary(visit, duty);
      const payout = getPayoutSummary(duty);
      const dayLabel = `Day ${duty?.day || dutyIndex + 1}  |  ${textValue(
        duty?.date,
        "-",
      )}`;

      y = ensureSpace(doc, dutyIndex > 0 ? y + 14 : y, 20);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(29, 78, 216);
      doc.text(dayLabel, 44, y);
      doc.setTextColor(20, 20, 20);
      y += 12;

      const dutyRows = [
        ["Service Status", textValue(duty?.serviceStatus)],
        ["Scheduled Start", formatDateTime(duty?.startTime)],
        ["Scheduled End", formatDateTime(duty?.endTime)],
        ["Check-in Time", formatDateTime(duty?.startDuty?.time)],
        ["Check-out Time", formatDateTime(duty?.endDuty?.time)],
        ...(normalizeStatus(duty?.payment?.status) !== "notrequired"
          ? [
              [
                "Daily Payment",
                `${textValue(duty?.payment?.status)} / ${formatAmount(
                  duty?.payment?.amount,
                )} / ${formatDateTime(duty?.payment?.paidDateTime)}`,
              ],
            ]
          : []),
        ["Total Patient Payment", formatAmount(transaction.patientPaid)],
        [
          "Payment Gateway Fee",
          formatDeductionAmount(transaction.gatewayDeductions),
        ],
        [
          "Ceviliyar Platform Fee",
          formatDeductionAmount(transaction.companyTotal),
        ],
        [
          "Gross Hospital Share",
          formatPerDayAmount(transaction.partnerGrossShare),
        ],
        [
          "Automated Transfer Fee",
          formatDeductionAmount(transaction.partnerTransferCharge),
        ],
        [
          "Net Payout to Hospital",
          formatPerDayAmount(transaction.partnerNetReceives),
        ],
        ["Partner Transfer ID", payout.vendorTransferId],
        ["Ceviliyar Transfer ID", payout.companyTransferId],
      ];
      y = drawKeyValueRows(doc, dutyRows, y);

      const serviceRows = (
        Array.isArray(duty?.services) ? duty.services : []
      ).map((service, serviceIndex) => [
        String(serviceIndex + 1),
        textValue(service?.name, "Service"),
        formatDateTime(service?.dateTime),
        formatServiceValues(service?.values ?? service?.value),
      ]);

      y = ensureSpace(doc, y + 10, 26);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Service Reports", 44, y);
      y += 12;

      y = drawSimpleTable(
        doc,
        ["#", "Service", "Reported At", "Details"],
        serviceRows,
        y,
        [24, 100, 100, 259],
      );
    });
  }

  const timeline = Array.isArray(visit?.timeline) ? visit.timeline : [];
  y = drawSectionTitle(doc, `Timeline (${timeline.length})`, y);
  if (!timeline.length) {
    y = drawKeyValueRows(doc, [["Timeline", "No timeline events"]], y);
  } else {
    const timelineRows = timeline.map((item, timelineIndex) => [
      String(timelineIndex + 1),
      textValue(item?.event),
      formatDateTime(item?.dateTime),
      textValue(item?.description),
      textValue(
        [item?.actor?.name, item?.actor?.role].filter(Boolean).join(" / "),
      ),
    ]);

    y = drawSimpleTable(
      doc,
      ["#", "Event", "When", "Description", "Actor"],
      timelineRows,
      y,
      [22, 90, 90, 210, 71],
    );
  }

  return y + 10;
};

export const downloadDetailedBookingsPdf = async ({
  visits = [],
  partner = null,
  sourceLabel = "Bookings",
} = {}) => {
  const exportVisits = filterExportableBookings(visits);

  if (!exportVisits.length) {
    const error = new Error(
      "No accepted, startDuty, endDuty, or completed bookings found for export.",
    );
    error.code = "NO_EXPORT_DATA";
    throw error;
  }

  const sortedVisits = [...exportVisits].sort((left, right) => {
    const leftTime = new Date(getCreatedAt(left) || 0).getTime();
    const rightTime = new Date(getCreatedAt(right) || 0).getTime();
    return rightTime - leftTime;
  });

  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
  });

  const generatedAt = moment().format("DD/MM/YYYY hh:mm A");
  const partnerDetails = getPartnerDetails(partner, sortedVisits);
  const summary = getExportSummary(sortedVisits);
  let y = await drawPartnerHeader(doc, partnerDetails, 34);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(30, 58, 95);
  doc.text("Detailed Booking Export", 40, y);
  y += 18;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(55, 65, 81);
  doc.text(`Generated At: ${generatedAt}`, 40, y);
  y += 22;
  y = drawSummaryCards(doc, summary, y);

  sortedVisits.forEach((visit, index) => {
    y = drawVisitBlock(doc, visit, index, y);
    if (index < sortedVisits.length - 1) {
      y = ensureSpace(doc, y, 30);
      doc.setDrawColor(226, 232, 240);
      doc.line(40, y, 555, y);
      y += 18;
    }
  });

  const transferIssueRows = getTransferIssueRows(sortedVisits);
  if (transferIssueRows.length) {
    y = drawSectionTitle(
      doc,
      `Pending / Exception Transfers (${transferIssueRows.length})`,
      y,
    );
    y = drawSimpleTable(
      doc,
      ["Visit ID", "Patient", "Duty Date", "Partner Amount", "Stored Status", "Action"],
      transferIssueRows,
      y,
      [70, 95, 70, 82, 72, 94],
    );
  }

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Page ${page} of ${pageCount}  |  Ceviliyar Detailed Booking Export`,
      40,
      doc.internal.pageSize.getHeight() - 18,
    );
  }

  const fileName = `${safeFilePart(sourceLabel, "bookings")}-detailed-${moment().format(
    "YYYYMMDD-HHmmss",
  )}.pdf`;
  doc.save(fileName);

  return {
    count: sortedVisits.length,
    fileName,
  };
};

export default downloadDetailedBookingsPdf;
